import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Guests, Templates, Shows as ShowsApi } from '../lib/api';

const AppContext = createContext(null);

const FALLBACK_SHOWS = [
  { id: 'wine-talks', name: 'Wine Talks with Paul K', short: 'WT', color: '#722F37', accent: '#C9A961', description: 'Conversations with winemakers, sommeliers and the people behind the bottle.' },
  { id: 'song-of-america', name: 'The Song of America with Paul K', short: 'SOA', color: '#1B2D5B', accent: '#B83A3A', description: 'Stories from the heart of American music and culture.' }
];

export const AppProvider = ({ children }) => {
  const [shows, setShows] = useState(FALLBACK_SHOWS);
  const [guests, setGuests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShowId, setActiveShowId] = useState(() => localStorage.getItem('pgm_activeShow') || FALLBACK_SHOWS[0].id);
  const [activeTable, setActiveTable] = useState('guests');
  const [activeView, setActiveView] = useState('dashboard');
  const [search, setSearch] = useState('');

  useEffect(() => { localStorage.setItem('pgm_activeShow', activeShowId); }, [activeShowId]);

  const refreshGuests = useCallback(async () => {
    try {
      const g = await Guests.list();
      setGuests(g);
    } catch (e) { console.error('Failed loading guests', e); }
  }, []);

  const refreshTemplates = useCallback(async () => {
    try {
      const t = await Templates.list();
      if (t?.length) setTemplates(t);
    } catch (e) { console.error('Failed loading templates', e); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [sh, t] = await Promise.all([ShowsApi.list(), Templates.list()]);
        if (sh?.length) setShows(sh);
        if (t?.length) setTemplates(t);
        await refreshGuests();
      } catch (e) {
        console.error('Initial load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshGuests]);

  // Refresh on window focus to pick up new bookings made via public page
  useEffect(() => {
    const onFocus = () => refreshGuests();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshGuests]);

  const [statusFilter, setStatusFilter] = useState('all');
  const activeShow = shows.find(s => s.id === activeShowId) || shows[0];

  const filteredGuests = guests.filter(g => g.showId === activeShowId).filter(g => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (g.name || '').toLowerCase().includes(q) ||
      (g.email || '').toLowerCase().includes(q) ||
      (g.episodeTitle || '').toLowerCase().includes(q) ||
      (g.company || '').toLowerCase().includes(q);
  }).filter(g => {
    // Dashboard stat-card click filters
    if (statusFilter === 'all') return true;
    if (statusFilter === 'upcoming') {
      // Confirmed for the future + invited (i.e. not yet recorded)
      return ['invited', 'confirmed'].includes(g.status);
    }
    if (statusFilter === 'pending-release') {
      return (g.releaseStatus || 'pending') !== 'signed' && g.status !== 'potential';
    }
    if (statusFilter === 'published') return g.status === 'published';
    if (statusFilter === 'completed') return ['recorded', 'edited', 'published'].includes(g.status);
    if (statusFilter === 'potential') return g.status === 'potential';
    if (statusFilter === 'no-email') return !(g.email || '').includes('@');
    if (statusFilter === 'missing-links') return ['recorded', 'edited', 'published'].includes(g.status) && !['apple', 'spotify', 'youtube'].every(k => ((g.platforms || {})[k] || '').trim());
    return true;
  });

  // Debounced guest saves — typing updates the UI instantly, one PATCH fires
  // 600ms after the last keystroke. Firing a request per keystroke caused
  // out-of-order responses to overwrite what the user was typing.
  const guestSaveTimers = useRef({});
  const guestPendingPatch = useRef({});

  const [thankYouPrompt, setThankYouPrompt] = useState(null); // guestId whose status just became "In Editing"

  const updateGuest = (id, patch) => {
    if (patch.status === 'edited') {
      const before = guests.find(g => g.id === id);
      if (before && before.status !== 'edited') setThankYouPrompt(id);
    }
    setGuests(prev => prev.map(g => g.id === id ? { ...g, ...patch } : g));
    guestPendingPatch.current[id] = { ...(guestPendingPatch.current[id] || {}), ...patch };
    clearTimeout(guestSaveTimers.current[id]);
    guestSaveTimers.current[id] = setTimeout(async () => {
      const toSend = guestPendingPatch.current[id];
      delete guestPendingPatch.current[id];
      try {
        const fresh = await Guests.update(id, toSend);
        // Merge server response but keep anything typed after the request left
        setGuests(prev => prev.map(g => g.id === id ? { ...fresh, ...(guestPendingPatch.current[id] || {}) } : g));
      } catch (e) {
        console.error(e);
        await refreshGuests();
      }
    }, 600);
  };

  const addGuest = async (g) => {
    try {
      const created = await Guests.create({ showId: activeShowId, ...g });
      setGuests(prev => [created, ...prev]);
      return created.id;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const removeGuest = async (id) => {
    setGuests(prev => prev.filter(g => g.id !== id));
    try { await Guests.remove(id); } catch (e) { await refreshGuests(); }
  };

  const signRelease = (id) => updateGuest(id, { releaseStatus: 'signed', signedDate: new Date().toISOString().slice(0,10) });

  return (
    <AppContext.Provider value={{
      loading, shows,
      guests, filteredGuests, refreshGuests,
      updateGuest, addGuest, removeGuest, signRelease,
      thankYouPrompt, setThankYouPrompt,
      templates, setTemplates, refreshTemplates,
      activeShow, activeShowId, setActiveShowId,
      activeTable, setActiveTable,
      activeView, setActiveView,
      search, setSearch,
      statusFilter, setStatusFilter
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
